import { Attachment, BodyType, DocumentVerificationV2, IntegrationRequest } from '@domain/types';
import type {
  IntegrationRequestV2Extended,
  PossibleDuplicateV2,
} from '@lifen-integration/features/integrationRequests/types/integrationRequest.types';
import { Http } from '@services/http';

type DocumentPage = {
  index: number;
};

type SplitDocument = {
  pages: DocumentPage[];
};

export type SplitDocumentPayload = {
  documents: SplitDocument[];
};

export async function splitIntegrationRequestDocument(
  documentId: string,
  payload: SplitDocumentPayload,
) {
  return await Http.getInstance().post({
    path: `/integrations/${documentId}/split`,
    body: payload as unknown as BodyType,
  });
}

export type SetDocumentFolderPayload = {
  ids: string[];
  folder?: string;
};

export async function setIntegrationRequestsFolder(payload: SetDocumentFolderPayload) {
  return await Http.getInstance().put({
    path: `/integrations/update_folder`,
    body: payload as unknown as BodyType,
  });
}

export async function applyTitleTemplate(
  id: IntegrationRequest['id'],
): Promise<{ documentTitle: string }> {
  return await Http.getInstance().post({
    path: `/integration_requests/${id}/apply_title_template`,
    version: 'v2',
  });
}

export interface AvailablePractitioner {
  lifenId: string;
  firstName: string;
  lastName: string;
  displayName: string;
  rpps?: string;
}

export async function fetchAvailablePractitioners(): Promise<AvailablePractitioner[]> {
  return await Http.getInstance().get({
    path: '/integration_requests/available_practitioners',
    version: 'v2',
  });
}

export async function fetchAvailablePractitioner(id: string): Promise<AvailablePractitioner> {
  return await Http.getInstance().get({
    path: `/integration_requests/available_practitioners/${id}`,
    version: 'v2',
  });
}

export async function searchAvailablePractitioners(
  searchText: string,
): Promise<AvailablePractitioner[]> {
  return await Http.getInstance().get({
    path: '/integration_requests/available_practitioners',
    version: 'v2',
    searchParams: { searchText },
  });
}

export async function fetchWorkspaceAvailablePractitioners(): Promise<AvailablePractitioner[]> {
  return await Http.getInstance().get({
    path: '/integration_requests/available_practitioners',
    version: 'v2',
  });
}

export async function dismissVerification(
  documentId: IntegrationRequest['id'],
  verificationId: DocumentVerificationV2['id'],
) {
  return await Http.getInstance().put({
    path: `/integrations/${documentId}/verifications/${verificationId.toString()}/dismiss`,
  });
}

export type MergeIntegrationRequestPayload = {
  documents: {
    uuid: string;
    rotate?: number;
  }[];
};

export async function mergeIntegrationRequests(payload: MergeIntegrationRequestPayload) {
  return await Http.getInstance().post({
    path: '/integration_requests/merge',
    version: 'v2',
    body: payload as unknown as BodyType,
  });
}

export async function fetchDocumentAttachment(id: number): Promise<Attachment> {
  return await Http.getInstance().get({
    path: `/document_attachments/${id}`,
    version: 'v2',
  });
}

/**
 * The detail document, straight from `GET /api/v2/integration_requests/:id`.
 *
 * `IntegrationRequestBlueprint` rendered with `view: :extended`: the list fields plus the
 * detail-only ones. No projection — this is the wire shape.
 */
export async function fetchIntegrationRequestV2Detail(
  id: string,
): Promise<IntegrationRequestV2Extended> {
  return await Http.getInstance().get({
    path: `/integration_requests/${id}`,
    version: 'v2',
  });
}

/**
 * A possible duplicate of `documentId`, from
 * `GET /api/v2/integration_requests/:documentId/possible_duplicates/:id`.
 *
 * Authorised on the SOURCE document (`documentId`), since the duplicate may belong to another
 * service. A miss of any kind is a 404.
 */
export async function fetchPossibleDuplicateV2(
  documentId: string,
  id: string,
): Promise<PossibleDuplicateV2> {
  return await Http.getInstance().get({
    path: `/integration_requests/${documentId}/possible_duplicates/${id}`,
    version: 'v2',
  });
}

/**
 * The duplicate's attachment. Not `fetchDocumentAttachment`: `/document_attachments/:id` authorises
 * on the caller's own documents and refuses a duplicate integrated by another service.
 */
export async function fetchPossibleDuplicateAttachment(
  documentId: string,
  id: string,
): Promise<Attachment> {
  return await Http.getInstance().get({
    path: `/integration_requests/${documentId}/possible_duplicates/${id}/attachment`,
    version: 'v2',
  });
}
